import React, { useState, useEffect, useMemo } from 'react';
import api, { adminAPI, API_BASE_URL } from '../../services/api';

const AVAILABLE_APPS = [
  { id: 'portfolio_blender', name: 'Portfolio Blender' },
  { id: 'research_reports', name: 'Research Reports' },
  { id: 'fund_screener', name: 'Fund Screener' },
  { id: 'portfolio_viewer', name: 'Portfolio Viewer' },
  { id: 'fact_sheets', name: 'Fact Sheets' },
  { id: 'portfolio_reports', name: 'Portfolio Reports' },
  { id: 'investment_perspectives', name: 'Investment Perspectives' },
  // Portfolio Attribution is internal-only (drummondcp.com) — intentionally
  // excluded from the admin grant list so it can't be assigned to client domains.
  // Analyst Portfolios is internal-only too (its API also requires a Drummond login),
  // so it is deliberately absent from this list.
  { id: 'resources', name: 'Resources' },
  { id: 'portfolio_constructor', name: 'Portfolio Constructor' },
  { id: 'portfolio_lab', name: 'Portfolio Lab' },
  { id: 'retirement_calculator', name: 'Retirement Calculator' },
  { id: 'allocation_calculator', name: 'Allocation Calculator' },
  { id: 'announcements', name: 'Announcements' },
  { id: 'portfolio_dashboard', name: 'Portfolio Dashboard' },
  { id: 'market_data', name: 'Market & Economic Data' },
  { id: 'cpd', name: 'CPD' },
];

// Resolve a logo URL to an absolute path (needed for dev where frontend/backend are on different ports)
const resolveLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
};

const DomainManager = () => {
  // Domain list state
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('general');

  // Global message
  const [message, setMessage] = useState(null);

  // New domain form
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [newDomainForm, setNewDomainForm] = useState({ domain: '', description: '' });
  const [isSavingNewDomain, setIsSavingNewDomain] = useState(false);

  // General tab state
  const [generalForm, setGeneralForm] = useState({ description: '', is_active: true });
  const [isSavingGeneral, setIsSavingGeneral] = useState(false);

  // Apps tab state
  const [permissions, setPermissions] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [isSavingApps, setIsSavingApps] = useState(false);
  // Per-user app permissioning (layered on the domain grant).
  const [restrictedApps, setRestrictedApps] = useState([]);  // app_names gated to selected users for this domain
  const [allUsers, setAllUsers] = useState([]);              // all users (filtered to the selected domain in render)
  const [userGrants, setUserGrants] = useState({});          // { app_name: Set(user_id) } — desired grants

  // Branding tab state
  const [brandingForm, setBrandingForm] = useState({
    company_name: '',
    primary_color: '#072040',
    secondary_color: '#f0a500',
    logo_url: '',
    logo_height: 40,
    eq_sma_provider: '',
  });
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [hasBranding, setHasBranding] = useState(false);

  // Portfolios tab state
  const [defaultPortfolios, setDefaultPortfolios] = useState([]);
  const [excludedPortfolios, setExcludedPortfolios] = useState(new Set());
  const [nameOverrides, setNameOverrides] = useState({});
  const [extraPortfolioCodes, setExtraPortfolioCodes] = useState([]);
  const [extraSearch, setExtraSearch] = useState('');
  const [isSavingPortfolios, setIsSavingPortfolios] = useState(false);
  const [availablePortfolios, setAvailablePortfolios] = useState([]);

  // Registration attempts (shown when no domain is selected)
  const [regAttempts, setRegAttempts] = useState(null);
  const [regDays, setRegDays] = useState(90);
  const [showAllAttempts, setShowAllAttempts] = useState(false);

  // Initial load
  useEffect(() => {
    fetchDomains();
    fetchDefaultPortfolios();
    fetchAvailablePortfolios();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    fetchRegistrationAttempts(regDays);
  }, [regDays]);

  const fetchRegistrationAttempts = async (days) => {
    try {
      const data = await adminAPI.getRegistrationAttempts(days);
      setRegAttempts(data);
    } catch (err) {
      console.error('Error fetching registration attempts:', err);
      setMessage({ type: 'error', text: 'Failed to load registration attempts' });
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/api/admin/users');
      setAllUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Load domain data when selection changes
  useEffect(() => {
    if (selectedDomain) {
      loadDomainData(selectedDomain.domain);
    }
  }, [selectedDomain]);

  const fetchDomains = async () => {
    setIsLoadingDomains(true);
    try {
      const data = await adminAPI.getAllowedDomains();
      setDomains(data);
    } catch (err) {
      console.error('Error fetching domains:', err);
      setMessage({ type: 'error', text: 'Failed to load domains' });
    } finally {
      setIsLoadingDomains(false);
    }
  };

  const fetchDefaultPortfolios = async () => {
    try {
      const data = await adminAPI.getDefaultPortfolios();
      setDefaultPortfolios(data);
    } catch (err) {
      console.error('Error fetching default portfolios:', err);
    }
  };

  const fetchAvailablePortfolios = async () => {
    try {
      const response = await api.get('/api/portfolio/portfolios');
      setAvailablePortfolios(response.data);
    } catch (err) {
      console.error('Error fetching available portfolios:', err);
    }
  };

  const loadDomainData = async (domain) => {
    // Load all domain-specific data in parallel
    try {
      const [permissionsRes, brandingRes, overridesRes, exclusionsRes, accessRes] = await Promise.allSettled([
        api.get('/api/admin/permissions'),
        api.get('/api/admin/brandings'),
        adminAPI.getDomainOverrides(domain),
        adminAPI.getDomainExclusions(domain),
        api.get('/api/admin/portfolio-access'),
      ]);

      // Set permissions for this domain
      if (permissionsRes.status === 'fulfilled') {
        const allPermissions = permissionsRes.value.data;
        setPermissions(allPermissions);
        const domainPerms = allPermissions.filter(p => p.domain === domain);
        setSelectedApps(domainPerms.filter(p => p.is_allowed).map(p => p.app_name));
        setRestrictedApps(domainPerms.filter(p => p.is_allowed && p.restrict_to_users).map(p => p.app_name));
      }

      // Load per-user app grants for this domain (for the "selected users only" lists)
      try {
        const grantsRes = await api.get(`/api/admin/user-app-permissions?domain=${encodeURIComponent(domain)}`);
        const byApp = {};
        (grantsRes.data || []).forEach(g => {
          if (!g.is_allowed) return;
          if (!byApp[g.app_name]) byApp[g.app_name] = new Set();
          byApp[g.app_name].add(g.user_id);
        });
        setUserGrants(byApp);
      } catch {
        setUserGrants({});
      }

      // Set branding for this domain
      if (brandingRes.status === 'fulfilled') {
        const allBrandings = brandingRes.value.data;
        const domainBranding = allBrandings.find(b => b.domain === domain);
        if (domainBranding) {
          setHasBranding(true);
          setBrandingForm({
            company_name: domainBranding.company_name || '',
            primary_color: domainBranding.primary_color || '#072040',
            secondary_color: domainBranding.secondary_color || '#f0a500',
            logo_url: domainBranding.logo_url || '',
            logo_height: domainBranding.logo_height ?? 40,
            eq_sma_provider: domainBranding.eq_sma_provider || '',
          });
        } else {
          setHasBranding(false);
          setBrandingForm({
            company_name: '',
            primary_color: '#072040',
            secondary_color: '#f0a500',
            logo_url: '',
            logo_height: 40,
            eq_sma_provider: '',
          });
        }
      }

      // Set portfolio overrides
      if (overridesRes.status === 'fulfilled') {
        const overrideMap = {};
        (overridesRes.value.overrides || []).forEach(o => {
          overrideMap[o.portfolio_code] = o.custom_name;
        });
        setNameOverrides(overrideMap);
      } else {
        setNameOverrides({});
      }

      // Set portfolio exclusions
      if (exclusionsRes.status === 'fulfilled') {
        setExcludedPortfolios(new Set(exclusionsRes.value.excluded_codes || []));
      } else {
        setExcludedPortfolios(new Set());
      }

      // Set extra portfolio access
      if (accessRes.status === 'fulfilled') {
        const allAccess = accessRes.value.data;
        const domainAccess = allAccess.find(a => a.domain === domain);
        setExtraPortfolioCodes(domainAccess?.portfolio_codes || []);
      } else {
        setExtraPortfolioCodes([]);
      }
    } catch (err) {
      console.error('Error loading domain data:', err);
    }
  };

  const handleSelectDomain = (domain) => {
    setSelectedDomain(domain);
    setGeneralForm({
      description: domain.description || '',
      is_active: domain.is_active,
    });
    setActiveTab('general');
    setMessage(null);
    setIsAddingDomain(false);
  };

  const handleStartAddDomain = () => {
    setIsAddingDomain(true);
    setSelectedDomain(null);
    setNewDomainForm({ domain: '', description: '' });
    setMessage(null);
  };

  const handleAllowBlockedDomain = (domain) => {
    setIsAddingDomain(true);
    setSelectedDomain(null);
    setNewDomainForm({ domain, description: '' });
    setMessage(null);
  };

  const handleCancelAddDomain = () => {
    setIsAddingDomain(false);
    setNewDomainForm({ domain: '', description: '' });
  };

  const handleAddDomain = async () => {
    if (!newDomainForm.domain) {
      setMessage({ type: 'error', text: 'Domain is required' });
      return;
    }

    setIsSavingNewDomain(true);
    try {
      await adminAPI.createAllowedDomain(newDomainForm.domain, newDomainForm.description);
      setMessage({ type: 'success', text: 'Domain added successfully!' });
      setIsAddingDomain(false);
      setNewDomainForm({ domain: '', description: '' });
      await fetchDomains();
      await fetchRegistrationAttempts(regDays);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add domain' });
    } finally {
      setIsSavingNewDomain(false);
    }
  };

  // General tab handlers
  const handleSaveGeneral = async () => {
    if (!selectedDomain) return;

    setIsSavingGeneral(true);
    try {
      await adminAPI.updateAllowedDomain(selectedDomain.id, {
        description: generalForm.description,
        is_active: generalForm.is_active,
      });
      setMessage({ type: 'success', text: 'General settings saved!' });
      await fetchDomains();
      // Update selected domain with new values
      setSelectedDomain(prev => ({
        ...prev,
        description: generalForm.description,
        is_active: generalForm.is_active,
      }));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save general settings' });
    } finally {
      setIsSavingGeneral(false);
    }
  };

  const handleDeleteDomain = async () => {
    if (!selectedDomain) return;
    if (!window.confirm(`Are you sure you want to delete "${selectedDomain.domain}"?\n\nThis will also remove all permissions, branding, and portfolio settings for this domain.`)) return;

    try {
      // Delete all related data
      await Promise.allSettled([
        adminAPI.deleteAllowedDomain(selectedDomain.id),
        api.delete(`/api/admin/brandings/${selectedDomain.domain}`),
        adminAPI.deleteDomainOverrides(selectedDomain.domain),
        adminAPI.deleteDomainExclusions(selectedDomain.domain),
        api.delete(`/api/admin/portfolio-access/${selectedDomain.domain}`),
      ]);

      // Delete permissions for this domain
      const domainPerms = permissions.filter(p => p.domain === selectedDomain.domain);
      await Promise.allSettled(domainPerms.map(p => api.delete(`/api/admin/permissions/${p.id}`)));

      setMessage({ type: 'success', text: 'Domain and all related settings deleted' });
      setSelectedDomain(null);
      await fetchDomains();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete domain' });
    }
  };

  // Apps tab handlers
  const handleAppToggle = (appId) => {
    setSelectedApps(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAllApps = () => {
    const allAppIds = AVAILABLE_APPS.map(app => app.id);
    setSelectedApps(prev =>
      prev.length === AVAILABLE_APPS.length ? [] : allAppIds
    );
  };

  // Toggle whether an app is gated to selected users (vs domain-wide) for this domain.
  const toggleRestrict = (appId) => {
    setRestrictedApps(prev =>
      prev.includes(appId) ? prev.filter(a => a !== appId) : [...prev, appId]
    );
  };

  // Toggle an individual user's grant for a per-user-restricted app.
  const toggleUserGrant = (appId, userId) => {
    setUserGrants(prev => {
      const next = { ...prev };
      const set = new Set(next[appId] || []);
      if (set.has(userId)) set.delete(userId); else set.add(userId);
      next[appId] = set;
      return next;
    });
  };

  const handleSaveApps = async () => {
    if (!selectedDomain) return;

    setIsSavingApps(true);
    try {
      // Get current permissions for this domain
      const domainPerms = permissions.filter(p => p.domain === selectedDomain.domain);

      // Delete all existing permissions for this domain
      await Promise.allSettled(domainPerms.map(p => api.delete(`/api/admin/permissions/${p.id}`)));

      // Add new permissions for selected apps (carrying the per-user gate flag)
      if (selectedApps.length > 0) {
        await Promise.allSettled(selectedApps.map(appId =>
          api.post('/api/admin/permissions', {
            domain: selectedDomain.domain,
            app_name: appId,
            is_allowed: true,
            restrict_to_users: restrictedApps.includes(appId),
          })
        ));
      }

      // Persist per-user grants for restricted+selected apps: grant ticked users,
      // revoke unticked ones (within this domain's user list).
      const domainUsers = allUsers.filter(u => u.domain === selectedDomain.domain);
      const grantOps = [];
      selectedApps
        .filter(appId => restrictedApps.includes(appId))
        .forEach(appId => {
          const granted = userGrants[appId] || new Set();
          domainUsers.forEach(u => {
            if (granted.has(u.id)) {
              grantOps.push(api.put('/api/admin/user-app-permissions', {
                user_id: u.id, app_name: appId, is_allowed: true,
              }));
            } else {
              grantOps.push(api.delete(
                `/api/admin/user-app-permissions?user_id=${u.id}&app_name=${encodeURIComponent(appId)}`
              ));
            }
          });
        });
      await Promise.allSettled(grantOps);

      setMessage({ type: 'success', text: 'App permissions saved!' });

      // Refresh permissions
      const response = await api.get('/api/admin/permissions');
      setPermissions(response.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save app permissions' });
    } finally {
      setIsSavingApps(false);
    }
  };

  // Branding tab handlers
  const handleBrandingChange = (field, value) => {
    setBrandingForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Please upload PNG, JPG, GIF, SVG, or WebP.' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File too large. Maximum size is 5MB.' });
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/api/admin/brandings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleBrandingChange('logo_url', response.data.logo_url);
      setMessage({ type: 'success', text: 'Logo uploaded!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveBranding = async () => {
    if (!selectedDomain) return;

    setIsSavingBranding(true);
    try {
      const brandingData = {
        domain: selectedDomain.domain,
        ...brandingForm,
      };

      if (hasBranding) {
        await api.put(`/api/admin/brandings/${selectedDomain.domain}`, brandingData);
      } else {
        await api.post('/api/admin/brandings', brandingData);
        setHasBranding(true);
      }
      setMessage({ type: 'success', text: 'Branding saved!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save branding' });
    } finally {
      setIsSavingBranding(false);
    }
  };

  const handleDeleteBranding = async () => {
    if (!selectedDomain || !hasBranding) return;
    if (!window.confirm('Are you sure you want to remove branding for this domain?')) return;

    try {
      await api.delete(`/api/admin/brandings/${selectedDomain.domain}`);
      setHasBranding(false);
      setBrandingForm({
        company_name: '',
        primary_color: '#072040',
        secondary_color: '#f0a500',
        logo_url: '',
        logo_height: 40,
        eq_sma_provider: '',
      });
      setMessage({ type: 'success', text: 'Branding removed' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove branding' });
    }
  };

  // Portfolios tab handlers
  const handleInclusionToggle = (portfolioCode) => {
    setExcludedPortfolios(prev => {
      const newSet = new Set(prev);
      if (newSet.has(portfolioCode)) {
        newSet.delete(portfolioCode);
      } else {
        newSet.add(portfolioCode);
      }
      return newSet;
    });
  };

  const handleNameOverrideChange = (portfolioCode, customName) => {
    setNameOverrides(prev => ({ ...prev, [portfolioCode]: customName }));
  };

  const handleRemoveExtraCode = (code) => {
    setExtraPortfolioCodes(prev => prev.filter(c => c !== code));
    setNameOverrides(prev => {
      const updated = { ...prev };
      delete updated[code];
      return updated;
    });
  };

  const handleSelectExtraPortfolio = (code) => {
    if (!extraPortfolioCodes.includes(code)) {
      setExtraPortfolioCodes(prev => [...prev, code]);
    }
  };

  const handleSavePortfolios = async () => {
    if (!selectedDomain) return;

    setIsSavingPortfolios(true);
    try {
      // Save name overrides
      const overrides = Object.entries(nameOverrides)
        .filter(([_, customName]) => customName && customName.trim())
        .map(([portfolio_code, custom_name]) => ({
          portfolio_code,
          custom_name: custom_name.trim()
        }));
      await adminAPI.setDomainOverrides(selectedDomain.domain, overrides);

      // Save exclusions
      await adminAPI.setDomainExclusions(selectedDomain.domain, Array.from(excludedPortfolios));

      // Save extra portfolio access
      if (extraPortfolioCodes.length > 0) {
        await api.post('/api/admin/portfolio-access', {
          domain: selectedDomain.domain,
          portfolio_codes: extraPortfolioCodes,
        });
      } else {
        // Delete if no extra codes
        try {
          await api.delete(`/api/admin/portfolio-access/${selectedDomain.domain}`);
        } catch (e) {
          // Ignore if doesn't exist
        }
      }

      setMessage({ type: 'success', text: 'Portfolio settings saved!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save portfolio settings' });
    } finally {
      setIsSavingPortfolios(false);
    }
  };

  const portfolioNameMap = useMemo(() => {
    const map = {};
    // Registry names first (covers portfolios with no Shadow feed yet), then
    // Shadow names take precedence where present.
    defaultPortfolios.forEach(d => { if (d.default_display_name) map[d.portfolio_code] = d.default_display_name; });
    availablePortfolios.forEach(p => { map[p.portfolio_code] = p.portfolio_name; });
    return map;
  }, [availablePortfolios, defaultPortfolios]);

  return (
    <div className="domain-manager">
      <h4 className="mb-2">Domain Manager</h4>
      <p className="text-muted mb-4">
        Manage domain registration, app access, branding, and portfolio settings in one place.
      </p>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <div className="row">
        {/* Left Panel - Domain List */}
        <div className="col-md-3">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Domains</h6>
              <button className="btn btn-primary btn-sm" onClick={handleStartAddDomain}>
                + Add
              </button>
            </div>
            <div className="card-body p-0">
              {isLoadingDomains ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm"></div>
                </div>
              ) : domains.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0 small">No domains configured</p>
              ) : (
                <div className="list-group list-group-flush" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {domains.map(domain => (
                    <button
                      key={domain.id}
                      className={`list-group-item list-group-item-action py-2 ${
                        selectedDomain?.id === domain.id ? 'active' : ''
                      }`}
                      onClick={() => handleSelectDomain(domain)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-bold small">{domain.domain}</div>
                          {domain.description && (
                            <small className={selectedDomain?.id === domain.id ? 'text-white-50' : 'text-muted'}>
                              {domain.description}
                            </small>
                          )}
                        </div>
                        {!domain.is_active && (
                          <span className="badge bg-secondary">Inactive</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Tabs */}
        <div className="col-md-9">
          {isAddingDomain ? (
            /* Add New Domain Form */
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Add New Domain</h6>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Domain *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newDomainForm.domain}
                    onChange={(e) => setNewDomainForm(prev => ({ ...prev, domain: e.target.value }))}
                    placeholder="clientcompany.com"
                  />
                  <small className="text-muted">Email domain for user registration</small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newDomainForm.description}
                    onChange={(e) => setNewDomainForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Client Company Name"
                  />
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={handleAddDomain}
                    disabled={isSavingNewDomain || !newDomainForm.domain}
                  >
                    {isSavingNewDomain ? 'Adding...' : 'Add Domain'}
                  </button>
                  <button className="btn btn-outline-secondary" onClick={handleCancelAddDomain}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : selectedDomain ? (
            /* Domain Configuration Tabs */
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                      onClick={() => setActiveTab('general')}
                    >
                      General
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'apps' ? 'active' : ''}`}
                      onClick={() => setActiveTab('apps')}
                    >
                      Apps {selectedApps.length > 0 && <span className="badge bg-primary ms-1">{selectedApps.length}</span>}
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'branding' ? 'active' : ''}`}
                      onClick={() => setActiveTab('branding')}
                    >
                      Branding {hasBranding && <span className="badge bg-success ms-1">✓</span>}
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'portfolios' ? 'active' : ''}`}
                      onClick={() => setActiveTab('portfolios')}
                    >
                      Portfolios
                    </button>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                {/* General Tab */}
                {activeTab === 'general' && (
                  <div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Domain</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedDomain.domain}
                        disabled
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={generalForm.description}
                        onChange={(e) => setGeneralForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Client Company Name"
                      />
                    </div>
                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="domainActive"
                          checked={generalForm.is_active}
                          onChange={(e) => setGeneralForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="domainActive">
                          Active (users can register)
                        </label>
                      </div>
                    </div>
                    <div className="mb-3 p-3 bg-light rounded small">
                      <div><strong>Added by:</strong> {selectedDomain.created_by || 'Unknown'}</div>
                      <div><strong>Added on:</strong> {selectedDomain.created_at ? new Date(selectedDomain.created_at).toLocaleDateString() : 'Unknown'}</div>
                    </div>
                    <div className="d-flex justify-content-between">
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveGeneral}
                        disabled={isSavingGeneral}
                      >
                        {isSavingGeneral ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={handleDeleteDomain}
                      >
                        Delete Domain
                      </button>
                    </div>
                  </div>
                )}

                {/* Apps Tab */}
                {activeTab === 'apps' && (
                  <div>
                    <p className="text-muted small mb-3">
                      Select which apps users from <strong>{selectedDomain.domain}</strong> can access.
                      New domains start with no apps enabled. Enable an app, then optionally switch it to
                      <em> "Selected users only"</em> to grant it to specific users on this domain rather than everyone.
                    </p>
                    <div className="d-flex justify-content-end mb-2">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={handleSelectAllApps}
                      >
                        {selectedApps.length === AVAILABLE_APPS.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="row g-2">
                      {(() => {
                        const isDrummondDomain = ['drummondcp.com', 'drummond.com'].includes(selectedDomain.domain);
                        const domainUsers = allUsers.filter(u => u.domain === selectedDomain.domain);
                        return AVAILABLE_APPS.map(app => {
                          const isSelected = selectedApps.includes(app.id);
                          const isRestricted = restrictedApps.includes(app.id);
                          const granted = userGrants[app.id] || new Set();
                          return (
                            <div key={app.id} className="col-md-6">
                              <div className={`border rounded p-3 ${isSelected ? 'bg-primary bg-opacity-10 border-primary' : ''}`}>
                                {/* Toggle is driven solely by the checkbox onChange (the label's
                                    htmlFor also triggers it). No onClick on the row — a parent
                                    onClick would double-fire with onChange and cancel the toggle. */}
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ cursor: 'pointer' }}
                                    checked={isSelected}
                                    onChange={() => handleAppToggle(app.id)}
                                    id={`app-${app.id}`}
                                  />
                                  <label className="form-check-label" style={{ cursor: 'pointer' }} htmlFor={`app-${app.id}`}>
                                    {app.name}
                                  </label>
                                </div>

                                {isSelected && !isDrummondDomain && (
                                  <div className="mt-2 ps-1" onClick={(e) => e.stopPropagation()}>
                                    <div className="form-check form-switch">
                                      <input
                                        type="checkbox"
                                        role="switch"
                                        className="form-check-input"
                                        checked={isRestricted}
                                        onChange={() => toggleRestrict(app.id)}
                                        id={`restrict-${app.id}`}
                                      />
                                      <label className="form-check-label small" htmlFor={`restrict-${app.id}`}>
                                        Selected users only
                                      </label>
                                    </div>

                                    {isRestricted && (
                                      <div className="mt-2 border-top pt-2">
                                        {domainUsers.length === 0 ? (
                                          <div className="text-muted small">No users registered on this domain yet.</div>
                                        ) : (
                                          domainUsers.map(u => (
                                            <div key={u.id} className="form-check small">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={granted.has(u.id)}
                                                onChange={() => toggleUserGrant(app.id, u.id)}
                                                id={`grant-${app.id}-${u.id}`}
                                              />
                                              <label className="form-check-label" htmlFor={`grant-${app.id}-${u.id}`}>
                                                {u.full_name || u.email}
                                              </label>
                                            </div>
                                          ))
                                        )}
                                        {domainUsers.length > 0 && granted.size === 0 && (
                                          <div className="text-warning small mt-1">
                                            No users selected — nobody on this domain will see {app.name}.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveApps}
                        disabled={isSavingApps}
                      >
                        {isSavingApps ? 'Saving...' : 'Save App Permissions'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Branding Tab */}
                {activeTab === 'branding' && (
                  <div>
                    <p className="text-muted small mb-3">
                      Configure white-label branding for <strong>{selectedDomain.domain}</strong>.
                      Leave blank to use default Drummond branding.
                    </p>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Company Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={brandingForm.company_name}
                        onChange={(e) => handleBrandingChange('company_name', e.target.value)}
                        placeholder="Client Company Pty Ltd"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Equity SMA Provider</label>
                      <input
                        type="text"
                        className="form-control"
                        value={brandingForm.eq_sma_provider}
                        onChange={(e) => handleBrandingChange('eq_sma_provider', e.target.value)}
                        placeholder="e.g. Praemium, HUB24"
                      />
                      <small className="text-muted">Overrides the fund name for Drummond Direct Equity holdings on factsheets</small>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Primary Colour</label>
                        <div className="input-group">
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={brandingForm.primary_color}
                            onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                            style={{ width: '50px' }}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={brandingForm.primary_color}
                            onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Secondary Colour</label>
                        <div className="input-group">
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={brandingForm.secondary_color}
                            onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                            style={{ width: '50px' }}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={brandingForm.secondary_color}
                            onChange={(e) => handleBrandingChange('secondary_color', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Logo</label>
                      <div className="d-flex gap-2 mb-2">
                        <label className="btn btn-outline-primary btn-sm" style={{ cursor: 'pointer' }}>
                          {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                            onChange={handleLogoUpload}
                            disabled={isUploadingLogo}
                            style={{ display: 'none' }}
                          />
                        </label>
                        {brandingForm.logo_url && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleBrandingChange('logo_url', '')}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={brandingForm.logo_url}
                        onChange={(e) => handleBrandingChange('logo_url', e.target.value)}
                        placeholder="Or enter URL manually"
                      />
                      {brandingForm.logo_url && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <small className="text-muted d-block mb-1">Preview:</small>
                          <img
                            src={resolveLogoUrl(brandingForm.logo_url)}
                            alt="Logo"
                            style={{ height: `${brandingForm.logo_height || 40}px`, width: 'auto' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      {brandingForm.logo_url && (
                        <div className="mt-3">
                          <label className="form-label fw-bold d-block">
                            Logo display height
                            <span className="text-muted fw-normal ms-2">({brandingForm.logo_height || 40} px)</span>
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="range"
                              className="form-range flex-grow-1"
                              min="20"
                              max="120"
                              step="1"
                              value={brandingForm.logo_height || 40}
                              onChange={(e) => handleBrandingChange('logo_height', parseInt(e.target.value, 10))}
                            />
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: '90px' }}
                              min="20"
                              max="200"
                              value={brandingForm.logo_height || 40}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                handleBrandingChange('logo_height', Number.isFinite(v) ? v : 40);
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              title="Reset to default (40 px)"
                              onClick={() => handleBrandingChange('logo_height', 40)}
                            >
                              Reset
                            </button>
                          </div>
                          <small className="text-muted">Controls the rendered logo height in the navbar and across the portal.</small>
                        </div>
                      )}
                    </div>
                    {/* Branding Preview */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Preview</label>
                      <div
                        className="p-3 rounded"
                        style={{ backgroundColor: brandingForm.primary_color, color: 'white' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          {brandingForm.logo_url && (
                            <img
                              src={resolveLogoUrl(brandingForm.logo_url)}
                              alt="Logo"
                              style={{ height: `${brandingForm.logo_height || 40}px`, width: 'auto' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <div>{brandingForm.company_name || 'Company'} Manager Research</div>
                            <div style={{ color: brandingForm.secondary_color }}>Sample Fund</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between">
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveBranding}
                        disabled={isSavingBranding}
                      >
                        {isSavingBranding ? 'Saving...' : 'Save Branding'}
                      </button>
                      {hasBranding && (
                        <button
                          className="btn btn-outline-danger"
                          onClick={handleDeleteBranding}
                        >
                          Remove Branding
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Portfolios Tab */}
                {activeTab === 'portfolios' && (
                  <div>
                    <p className="text-muted small mb-3">
                      Configure portfolio visibility and custom names for <strong>{selectedDomain.domain}</strong>.
                    </p>

                    {/* Default Portfolios */}
                    <h6>Default Portfolios</h6>
                    <p className="text-muted small">Uncheck to hide from this domain. Enter custom names if needed.</p>
                    <div className="table-responsive mb-4">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th style={{ width: '60px' }}>Show</th>
                            <th style={{ width: '120px' }}>Code</th>
                            <th>Default Name</th>
                            <th>Custom Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {defaultPortfolios.filter(dp => !dp.tailored).map(dp => {
                            const isIncluded = !excludedPortfolios.has(dp.portfolio_code);
                            return (
                              <tr key={dp.portfolio_code} className={!isIncluded ? 'table-secondary' : ''}>
                                <td className="text-center">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isIncluded}
                                    onChange={() => handleInclusionToggle(dp.portfolio_code)}
                                  />
                                </td>
                                <td><code className="small">{dp.portfolio_code}</code></td>
                                <td className="text-muted small">{dp.default_display_name}</td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={nameOverrides[dp.portfolio_code] || ''}
                                    onChange={(e) => handleNameOverrideChange(dp.portfolio_code, e.target.value)}
                                    placeholder={dp.default_display_name}
                                    disabled={!isIncluded}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                          {extraPortfolioCodes.length > 0 && (
                            <>
                              <tr>
                                <td colSpan="4" className="text-center fw-bold small text-muted bg-light" style={{ letterSpacing: '0.05em' }}>
                                  Additional Portfolios
                                </td>
                              </tr>
                              {extraPortfolioCodes.map(code => {
                                const defaultName = portfolioNameMap[code] || code;
                                return (
                                  <tr key={`extra-${code}`}>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm py-0 px-1"
                                        style={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                                        title={`Remove ${code}`}
                                        onClick={() => handleRemoveExtraCode(code)}
                                      >
                                        &times;
                                      </button>
                                    </td>
                                    <td><code className="small">{code}</code></td>
                                    <td className="text-muted small">{defaultName}</td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={nameOverrides[code] || ''}
                                        onChange={(e) => handleNameOverrideChange(code, e.target.value)}
                                        placeholder={defaultName}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Extra Portfolios — searchable selection (same UX as the Portfolio Viewer) */}
                    <h6>Extra Portfolios</h6>
                    <p className="text-muted small">Grant access to additional portfolios beyond the defaults. Search and tick to add.</p>
                    <input
                      type="text"
                      className="form-control form-control-sm mb-2"
                      value={extraSearch}
                      onChange={(e) => setExtraSearch(e.target.value)}
                      placeholder="Search portfolios by name or code…"
                    />
                    <div className="mb-3" style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                      {(() => {
                        const defaultCodeSet = new Set(defaultPortfolios.filter(d => !d.tailored).map(d => d.portfolio_code));
                        const tailoredCodeSet = new Set(defaultPortfolios.filter(d => d.tailored).map(d => d.portfolio_code));
                        const q = extraSearch.trim().toLowerCase();
                        // Addable universe = Shadow-held portfolios PLUS any active
                        // registry portfolio not already present (e.g. a tailored
                        // portfolio with no Shadow feed yet). Registry viability, not
                        // Shadow holdings, gates granting a tailored portfolio.
                        const byCode = {};
                        availablePortfolios.forEach(p => { byCode[p.portfolio_code] = p; });
                        defaultPortfolios.forEach(d => {
                          if (d.is_active && !byCode[d.portfolio_code]) {
                            byCode[d.portfolio_code] = {
                              portfolio_code: d.portfolio_code,
                              portfolio_name: d.default_display_name || d.portfolio_code,
                              platform: null,
                            };
                          }
                        });
                        const list = Object.values(byCode).filter(p => {
                          if (defaultCodeSet.has(p.portfolio_code)) return false;
                          if (!q) return true;
                          return (p.portfolio_name || '').toLowerCase().includes(q)
                            || (p.portfolio_code || '').toLowerCase().includes(q);
                        });
                        if (list.length === 0) {
                          return <div className="text-muted small p-2">No portfolios found</div>;
                        }
                        return list.map(p => {
                          const checked = extraPortfolioCodes.includes(p.portfolio_code);
                          return (
                            <label
                              key={p.portfolio_code}
                              className="d-flex align-items-center gap-2 px-2 py-1"
                              style={{ cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid #f0f0f0' }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input m-0 flex-shrink-0"
                                checked={checked}
                                onChange={() => (checked
                                  ? handleRemoveExtraCode(p.portfolio_code)
                                  : handleSelectExtraPortfolio(p.portfolio_code))}
                              />
                              <span className="flex-grow-1 text-truncate">{p.portfolio_name || p.portfolio_code}</span>
                              {tailoredCodeSet.has(p.portfolio_code) && (
                                <span className="badge bg-warning text-dark flex-shrink-0" style={{ fontSize: '0.62rem' }}
                                  title="Tailored — not a default; granted to this client group by request">Tailored</span>
                              )}
                              {p.platform && (
                                <span className="text-muted flex-shrink-0" style={{ fontSize: '0.7rem' }}>{p.platform}</span>
                              )}
                              <code className="text-muted flex-shrink-0" style={{ fontSize: '0.72rem' }}>{p.portfolio_code}</code>
                            </label>
                          );
                        });
                      })()}
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn btn-primary"
                        onClick={handleSavePortfolios}
                        disabled={isSavingPortfolios}
                      >
                        {isSavingPortfolios ? 'Saving...' : 'Save Portfolio Settings'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* No domain selected */
            <>
              <p className="text-muted small mb-3">
                Select a domain from the list or click "+ Add" to create a new one.
              </p>

              {/* Registration attempts */}
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Blocked Registration Attempts</h6>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={regDays}
                    onChange={(e) => setRegDays(Number(e.target.value))}
                  >
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last 12 months</option>
                  </select>
                </div>
                <div className="card-body p-0">
                  {!regAttempts ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm"></div>
                    </div>
                  ) : regAttempts.blocked_domains.length === 0 ? (
                    <p className="text-muted text-center py-4 mb-0 small">
                      No one from an unapproved domain has tried to register in this period
                    </p>
                  ) : (
                    <table className="table table-sm mb-0 small align-middle">
                      <thead>
                        <tr>
                          <th className="ps-3">Domain</th>
                          <th>Emails</th>
                          <th className="text-end">Attempts</th>
                          <th>Last attempt</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {regAttempts.blocked_domains.map(row => (
                          <tr key={row.domain}>
                            <td className="ps-3 fw-bold">{row.domain}</td>
                            <td>{row.emails.join(', ')}</td>
                            <td className="text-end">{row.attempts}</td>
                            <td>{new Date(row.last_attempt).toLocaleString('en-AU')}</td>
                            <td className="text-end pe-3">
                              <button
                                className="btn btn-outline-primary btn-sm py-0"
                                onClick={() => handleAllowBlockedDomain(row.domain)}
                              >
                                Allow
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {regAttempts && regAttempts.attempts.length > 0 && (
                  <div className="card-footer bg-white">
                    <button
                      className="btn btn-link btn-sm p-0"
                      onClick={() => setShowAllAttempts(v => !v)}
                    >
                      {showAllAttempts ? 'Hide' : 'Show'} all registration attempts ({regAttempts.attempts.length})
                    </button>
                    {showAllAttempts && (
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="mt-2">
                        <table className="table table-sm mb-0 small">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Email</th>
                              <th>Name</th>
                              <th>Outcome</th>
                              <th>IP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {regAttempts.attempts.map(a => (
                              <tr key={a.id}>
                                <td className="text-nowrap">{new Date(a.created_at).toLocaleString('en-AU')}</td>
                                <td>{a.email}</td>
                                <td>{a.full_name}</td>
                                <td>
                                  <span className={`badge ${
                                    a.outcome === 'success' ? 'bg-success'
                                      : a.outcome === 'domain_not_allowed' ? 'bg-warning text-dark'
                                      : 'bg-secondary'
                                  }`}>
                                    {a.outcome.replace(/_/g, ' ')}
                                  </span>
                                  {a.outcome === 'domain_not_allowed' && a.domain_now_allowed && (
                                    <span className="text-muted ms-1">(since allowed)</span>
                                  )}
                                </td>
                                <td className="text-muted">{a.ip_address}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-4 p-3 bg-light rounded small">
        <h6>How Domain Management Works</h6>
        <ul className="mb-0 text-muted">
          <li><strong>General:</strong> Enable/disable registration for this domain</li>
          <li><strong>Apps:</strong> Select which apps users from this domain can access (new domains start with none)</li>
          <li><strong>Branding:</strong> Configure white-label colours and logo (optional)</li>
          <li><strong>Portfolios:</strong> Control which portfolios are visible and set custom names</li>
          <li><strong>Tailored portfolios</strong> aren&rsquo;t shown by default &mdash; assign them to a client group under <strong>Extra Portfolios</strong> (look for the &ldquo;Tailored&rdquo; badge)</li>
          <li><strong>drummondcp.com</strong> users have full access to everything by default</li>
        </ul>
      </div>
    </div>
  );
};

export default DomainManager;
